import type { HttpAdapter, RequestConfig, HttpResponse } from '@child-nav/shared/services/api';

/**
 * Fetch-based HTTP Adapter for the web package.
 * Implements the same HttpAdapter interface as the mobile AxiosHttpAdapter
 * but uses the native browser fetch API — no extra dependencies required.
 */
export class FetchHttpAdapter implements HttpAdapter {
  private baseUrl: string;
  private authToken: string | null = null;
  private defaultTimeout: number;

  constructor(baseUrl: string, defaultTimeout: number = 30000) {
    // Strip trailing slash so we can safely prefix paths that start with /
    this.baseUrl = baseUrl.replace(/\/$/, '');
    this.defaultTimeout = defaultTimeout;
  }

  setAuthToken(token: string | null): void {
    this.authToken = token;
    console.log(`🔑 FetchHttpAdapter: Auth token ${token ? 'set' : 'cleared'}`);
  }

  getAuthToken(): string | null {
    return this.authToken;
  }

  private buildHeaders(extra?: Record<string, string>): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...extra,
    };
    if (this.authToken) {
      headers['Authorization'] = `Bearer ${this.authToken}`;
    }
    return headers;
  }

  private async fetchWithTimeout(
    url: string,
    options: RequestInit,
    timeout: number
  ): Promise<Response> {
    const controller = new AbortController();
    const id = setTimeout(() => {
      console.error(`⏰ FetchHttpAdapter: TIMEOUT after ${timeout}ms — aborting ${options.method} ${url}`);
      controller.abort();
    }, timeout);
    console.log(`⏳ FetchHttpAdapter: calling fetch()`, { url, method: options.method, timeout });
    try {
      const response = await fetch(url, { ...options, signal: controller.signal });
      console.log(`✅ FetchHttpAdapter: fetch() resolved`, { url, status: response.status, ok: response.ok, type: response.type });
      return response;
    } catch (err: any) {
      console.error(`❌ FetchHttpAdapter: fetch() threw`, { url, name: err?.name, message: err?.message, err });
      throw err;
    } finally {
      clearTimeout(id);
    }
  }

  private async handleResponse<T>(response: Response): Promise<HttpResponse<T>> {
    const text = await response.text();
    let data: T;
    try {
      data = JSON.parse(text) as T;
   } catch {
      data = text as unknown as T;
    }

    if (!response.ok) {
      console.error(`❌ FetchHttpAdapter: HTTP error ${response.status} ${response.statusText}`);
      const error: any = new Error(`HTTP ${response.status}: ${response.statusText}`);
      error.response = {
        data,
        status: response.status,
        statusText: response.statusText,
      };
      throw error;
    }

    const headers: Record<string, string> = {};
    response.headers.forEach((value: string, key: string) => { headers[key] = value; });

    return { data, status: response.status, statusText: response.statusText, headers };
  }

  async get<T = any>(url: string, config?: RequestConfig): Promise<HttpResponse<T>> {
    const fullUrl = url.startsWith('http') ? url : `${this.baseUrl}${url}`;
    const response = await this.fetchWithTimeout(
      fullUrl,
      { method: 'GET', headers: this.buildHeaders(config?.headers) },
      config?.timeout ?? this.defaultTimeout
    );
    return this.handleResponse<T>(response);
  }

  async post<T = any>(url: string, data?: any, config?: RequestConfig): Promise<HttpResponse<T>> {
    const fullUrl = url.startsWith('http') ? url : `${this.baseUrl}${url}`;
    const bodyStr = data !== undefined ? JSON.stringify(data) : undefined;
    let response: Response;
    try {
      response = await this.fetchWithTimeout(
        fullUrl,
        {
          method: 'POST',
          headers: this.buildHeaders(config?.headers),
          body: bodyStr,
        },
        config?.timeout ?? this.defaultTimeout
      );
    } catch (err: any) {
      console.error(`❌ FetchHttpAdapter POST failed before response:`, { url: fullUrl, name: err?.name, message: err?.message });
      throw err;
    }
    return this.handleResponse<T>(response);
  }

  async put<T = any>(url: string, data?: any, config?: RequestConfig): Promise<HttpResponse<T>> {
    const fullUrl = url.startsWith('http') ? url : `${this.baseUrl}${url}`;
    const response = await this.fetchWithTimeout(
      fullUrl,
      {
        method: 'PUT',
        headers: this.buildHeaders(config?.headers),
        body: data !== undefined ? JSON.stringify(data) : undefined,
      },
      config?.timeout ?? this.defaultTimeout
    );
    return this.handleResponse<T>(response);
  }

  async delete<T = any>(url: string, config?: RequestConfig): Promise<HttpResponse<T>> {
    const fullUrl = url.startsWith('http') ? url : `${this.baseUrl}${url}`;
    const response = await this.fetchWithTimeout(
      fullUrl,
      { method: 'DELETE', headers: this.buildHeaders(config?.headers) },
      config?.timeout ?? this.defaultTimeout
    );
    return this.handleResponse<T>(response);
  }
}
